import { useState, useContext, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppContext } from '../components/AppContext.tsx'
import { apiClient } from '../lib/apiClient'
import { toast } from 'sonner'
import { SwitchTransition, CSSTransition } from 'react-transition-group'

// Import onboarding components
import ApiKeyConfiguration from '../components/onboarding/ApiKeyConfiguration.jsx'
import CommandInterface from '../components/onboarding/CommandInterface.jsx'
import ProgressIndicator from '../components/onboarding/ProgressIndicator.jsx'
import NavigationButtons from '../components/onboarding/NavigationButtons.jsx'
import CmdMenu from '../components/CmdMenu/CmdMenu.jsx'
import { KeybindingProvider } from '../hooks/keybinding/KeybindingContext.jsx'
import AppKeybindings from '../components/AppKeybindings.jsx'
import { isElectron } from '../utils/platform.js'

const OnboardingPage = () => {
  const navigate = useNavigate()
  const { authSession, setSearchModal, setProfile } = useContext(AppContext)
  const [currentStep, setCurrentStep] = useState(1)
  const [isLoading, setIsLoading] = useState(false)

  const totalSteps = 2

  // Step configuration
  const steps = [
    { id: 1, name: 'API Keys', component: ApiKeyConfiguration },
    { id: 2, name: 'Command Interface', component: CommandInterface }
  ]

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1)
    } else {
      completeOnboarding()
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const completeOnboarding = async () => {
    setIsLoading(true)
    try {
      const updatedProfile = await apiClient.patch('/api/profile/onboarding-complete', { completed: true })
      
      // Update profile in context to prevent redirect loop
      setProfile(updatedProfile)
      
      // Navigate to main app
      navigate('/chat', { replace: true })
    } catch (error) {
      toast.error('Failed to complete onboarding. Please try again.')
      console.error('Onboarding error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGetStarted = () => {
    completeOnboarding()
  }

  // Prevent access if not authenticated
  useEffect(() => {
    if (!authSession) {
      navigate('/login', { replace: true })
    }
  }, [authSession, navigate])
  
  // Custom keyboard handler for onboarding
  useEffect(() => {
    const handleOnboardingKeyboard = (e) => {
      // Only respond to ⌘K on the final step
      if (e.metaKey && e.code === 'KeyK') {
        e.preventDefault()
        e.stopPropagation() // Prevent the global handler from also firing
        
        if (currentStep === totalSteps) {
          setSearchModal()
        }
      }
    }
    
    // Add our handler with capture phase to intercept before global handler
    document.addEventListener('keydown', handleOnboardingKeyboard, true)
    
    return () => {
      document.removeEventListener('keydown', handleOnboardingKeyboard, true)
    }
  }, [currentStep, totalSteps, setSearchModal])

  if (!authSession) {
    return null
  }

  // Get current step component
  const CurrentStepComponent = steps[currentStep - 1].component
  const nodeRef = useRef(null)

  // Props for navigation and progress components
  const navigationProps = {
    currentStep,
    totalSteps,
    onPrevious: handlePrevious,
    onNext: handleNext,
    isLoading,
    authSession
  }

  const progressProps = {
    currentStep,
    totalSteps
  }

  return (
    <KeybindingProvider>
      <AppKeybindings />
      {/* Electron: draggable title bar region (38px) for window dragging */}
      {isElectron() && (
        <div
          className="fixed left-0 right-0 top-0 z-50 h-[38px] bg-transparent"
          style={{ WebkitAppRegion: 'drag' }}
          aria-hidden
        />
      )}
      {/* Keybinding system UI components */}
      <div className="fixed inset-0 bg-page overflow-hidden">
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={currentStep}
            nodeRef={nodeRef}
            timeout={300}
            classNames="fade"
          >
            <div ref={nodeRef} className="absolute inset-0">
              <CurrentStepComponent
                renderNavigation={(props = navigationProps) => (
                  <NavigationButtons {...props} />
                )}
                renderProgress={(props = progressProps) => (
                  <ProgressIndicator {...props} />
                )}
                navigationProps={navigationProps}
                progressProps={progressProps}
                onGetStarted={handleGetStarted}
              />
            </div>
          </CSSTransition>
        </SwitchTransition>
        
        {/* Only show CmdMenu on the final step (Command Interface) */}
        {currentStep === totalSteps && <CmdMenu />}
      </div>
    </KeybindingProvider>
  )
}

export default OnboardingPage
